import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { CoreSidebarService } from '@core/components/core-sidebar/core-sidebar.service';
import { TranslocoService } from '@jsverse/transloco';
import { ColumnMode, DatatableComponent, SelectionType } from '@swimlane/ngx-datatable';
import { IColumnSettings } from 'app/main/shared/shared/table/components/custom-columns-settings/column-settings';
import { map, Observable, Subject, take } from 'rxjs';
import Swal from 'sweetalert2';
import { ITablePagerOptions } from '../../../shared/shared/table/components/table-pager/ITablePagerOptions';
import { IActivateEvent } from '../dto/datatable.dto';
import { IDocumentDto } from '../dto/document-listing.dto';
import { DocumentListingFacade } from '../store/document-listing.facade';

@Component({
    selector: 'app-document-listing',
    templateUrl: './document-listing.component.html',
    styleUrls: ['./document-listing.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class DocumentListingComponent implements OnInit, OnDestroy {

    @ViewChild(DatatableComponent) table: DatatableComponent;

    @Input() selectable: boolean = false
    @Input() tableColumns$: Observable<IColumnSettings[]>;
    @Output() rowClicked: EventEmitter<IDocumentDto> = new EventEmitter<IDocumentDto>();

    private untilDestroy$ = new Subject<void>();
    public ColumnMode = ColumnMode;
    protected SelectionType = SelectionType;

    public documentsList$: Observable<IDocumentDto[]> = this._documentListingFacade.documentsList$;
    public paging$: Observable<ITablePagerOptions> = this._documentListingFacade.paging$;
    public isLoading$: Observable<boolean> = this._documentListingFacade.isLoading$;
    public search$: Observable<string> = this._documentListingFacade.search$;
    public selection$ = this._documentListingFacade.selection$;
    public isDeletingDocumentsInProgress$ = this._documentListingFacade.isDeletingDocumentsInProgress$;
    public isRestoringDocumentsInProgress$ = this._documentListingFacade.isRestoringDocumentsInProgress$;
    public mappedColumns$ = this._documentListingFacade.mappedColumns$;

    /**
     *
     * @param _coreSidebarService
     * @param _documentListingFacade
     * @param _transloco
     */
    constructor(
        private _coreSidebarService: CoreSidebarService,
        private _documentListingFacade: DocumentListingFacade,
        private _translocoService: TranslocoService
    ) {
    }

    // Public Methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle Sidebar
     *
     * @param nameRef
     */
    createNewTodo(nameRef, closeNameRef): void {
        this._coreSidebarService.getSidebarRegistry(nameRef).toggleOpen();
        this._coreSidebarService.getSidebarRegistry(closeNameRef).toggleOpen();
    }


    // Lifecycle Hooks
    // -----------------------------------------------------------------------------------------------------
    /**
     * On init
     */
    ngOnInit(): void {
        this._documentListingFacade.fetchDocuments();
    }




    /**
     * Handles the column reorder event in the document listing table.
     * 
     * This method is triggered when a column is reordered by the user. It updates
     * the column settings in the store to reflect the new order.
     *
     * @param $event - The event containing the new and previous column indices.
     */
    onColumnReorder($event) {
        this.tableColumns$.pipe(take(1)).subscribe(columns => {
            const { newValue, prevValue } = $event;
            const movedColumn = columns.splice(prevValue, 1)[0];
            columns.splice(newValue, 0, movedColumn);
            this._documentListingFacade.updateColumnsSettings(columns, false);
        });
    }

    onColumnResize($event: { column: { prop: string }, newValue: number }): void {
        // Guard against malformed events
        if (!$event || !$event?.column || !$event?.column?.prop || typeof $event?.newValue !== 'number') {
            // nothing to do when event or column or newValue is missing
            return;
        }

        const { column, newValue } = $event;
        const columnProp = column?.prop;

        this.tableColumns$.pipe(take(1)).subscribe(columns => {
            // be defensive about column objects in the columns array
            const matchBy = (col: IColumnSettings | undefined) =>
                !!col && (
                    col.field_name === columnProp
                    || (col.field_name === 'document_type' && ['visible_name', 'name'].includes(columnProp))
                );

            const colIndex = columns.findIndex(matchBy);
            if (colIndex === -1) return;
            const updatedColumns = columns.map((c, i) => i === colIndex ? { ...c, width: newValue } : c );
            this._documentListingFacade.updateColumnsSettings(updatedColumns, false);
        });
    }

    ngOnDestroy(): void {
        this.untilDestroy$.next();
        this.untilDestroy$.complete();
    }

    onPageChange(pageNumber: number): void {
        this._documentListingFacade.changePage(pageNumber);
    }



    /**
     * Handles the activation event for a row in the document listing.
     * 
     * This method is triggered when an activation event occurs (e.g., a click).
     * If the event type is 'click', it retrieves the clicked row from the event
     * and invokes the `onRowClicked` method with the clicked row as an argument.
     *
     * @param event - The activation event containing the type and the row data.
     */
    onActivate(event: IActivateEvent): void {
        if (event.type === 'click') {
            this.rowClicked.emit(event.row)
        }
    }

    onSelectionChange(documents: IDocumentDto[]) {
        this._documentListingFacade.selectionChanged(documents);
    }

    async onDeleteSelectedDocumentsClick() {
        const { isConfirmed } = await this.confirmActionModal(this._translocoService.translate('Document deletion confirmation text (delete document modal on document page) - Do you want to delete all selected documents'));
        if (isConfirmed) {
            this._documentListingFacade.deleteSelectedDocuments();
        }
    }

    async onRestoreSelectedDocumentsClick() {
        const { isConfirmed } = await this.confirmActionModal(this._translocoService.translate('Document restore confirmation text (delete document modal on document page) - Do you want to restore all selected documents'));
        if (isConfirmed) {
            this._documentListingFacade.restoreSelectedDocuments();
        }
    }

    /**
     * Display confirmation modal. Returns standard Saal Promise
     */
    private async confirmActionModal(confirmText: string) {
        return await Swal.fire({
            title: this._translocoService.translate('Document confirmation modal title  (delete document modal on document page) - Are you sure'),
            text: confirmText,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: this._translocoService.translate('Confirmation modal button - yes'),
            confirmButtonColor: '#ef0016',
            cancelButtonText: this._translocoService.translate('Confirmation modal button - no'),
        });
    }
}
